import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  CheckIcon, 
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import RecaptchaCheckbox from '../components/RecaptchaCheckbox';
import { getRecaptchaSiteKey } from '../utils/recaptcha';

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [addingToCart, setAddingToCart] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const recaptchaRef = useRef(null);

  // Handle hash navigation scrolling
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const hostingPlans = {
    shared: {
      name: 'Shared Hosting',
      price: { monthly: 20, yearly: 200 },
      savings: { yearly: 40 },
      description: 'Best for: Personal blogs, small websites',
      features: [
        'White-Glove Onboarding & Migration',
        'Domain Registration Assistance',
        'Free SSL & Daily Malware Scans',
        'Downtime Monitoring',
        'Basic Support via Email',
        'Plesk Dashboard Access',
        'Additional Cost for Server Troubleshooting'
      ]
    },
    private: {
      name: 'Private Hosting',
      price: { monthly: 25, yearly: 250 },
      savings: { yearly: 50 },
      description: 'Best for: Growing businesses or medium-traffic sites',
      features: [
        'White-Glove Onboarding & Migration',
        'Domain Registration Assistance',
        'Dedicated Resources (RAM, CPU)',
        'Enhanced Security & Privacy',
        'Free SSL & Daily Malware Scans',
        'Downtime Monitoring',
        'Priority Email Support',
        'Daily Backups',
        'Additional Cost for Server Troubleshooting'
      ]
    },
    premium: {
      name: 'Premium Hosting',
      price: { monthly: 40, yearly: 400 },
      savings: { yearly: 80 },
      description: 'Best for: E-commerce or high-traffic websites',
      features: [
        'All features of Private Hosting plus:',
        'Advanced CloudFlare Caching',
        'High Priority Phone & Email support',
        'First Year Free Domain Registration ($50 Limit)',
        '1 Hour/Month of Dedicated Server Troubleshooting'
      ]
    }
  };

  const handleAddToCart = async (planType, billingCycle) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return;
    }

    setAddingToCart(prev => ({ ...prev, [`${planType}-${billingCycle}`]: true }));
    
    try {
      await api.post('/api/cart/add', { planType, billingCycle });
      
      // Dispatch custom event to notify navbar of cart update
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      
      // Custom toast with longer duration and cart button
      toast.custom(
        (t) => (
          <div
            className={`${
              t.visible ? 'animate-enter' : 'animate-leave'
            } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CheckIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {hostingPlans[planType].name} added to cart!
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Ready to complete your purchase?
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/cart');
                }}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <ShoppingCartIcon className="h-5 w-5 mr-2" />
                View Cart
              </button>
            </div>
          </div>
        ),
        {
          duration: 5000, // 5 seconds
          position: 'top-right',
        }
      );
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(prev => ({ ...prev, [`${planType}-${billingCycle}`]: false }));
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!contactForm.name || !contactForm.email) {
      toast.error('Please fill in all required fields (name and email)');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const recaptchaToken = recaptchaRef.current?.getValue() || '';
    if (!recaptchaToken) {
      if (getRecaptchaSiteKey() || process.env.NODE_ENV === 'production') {
        toast.error('Please complete the reCAPTCHA');
        return;
      }
    }

    setIsSubmittingContact(true);
    
    try {
      // Send form data with fixed subject
      await api.post('/api/contact/submit', {
        ...contactForm,
        subject: 'The Host Bros Contact Form Submission',
        recaptchaToken
      });
      toast.success('Thank you for contacting us! We will get back to you soon.');
      setContactForm({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      recaptchaRef.current?.reset();
    } catch (error) {
      recaptchaRef.current?.reset();
      if (!error.response && /recaptcha/i.test(error.message || '')) {
        toast.error('Unable to verify you are human. Please refresh and try again.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to submit contact form. Please try again.');
      }
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>The Host Bros - Professional Web Hosting Services</title>
        <meta name="description" content="Professional web hosting services with 99.9% uptime guarantee. Shared, Private, and Premium hosting plans available." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Professional WordPress & E-commerce Web Hosting
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-primary-100">
                Fast, reliable, and secure hosting for your website
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/cart"
                  className="btn-secondary text-lg px-8 py-4"
                >
                  View Cart
                </Link>
                <Link
                  to={isAuthenticated ? "/dashboard" : "/login"}
                  className="btn-primary text-lg px-8 py-4"
                >
                  {isAuthenticated ? "Go to Dashboard" : "Get Started"}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose The Host Bros?
              </h2>
              <p className="text-lg text-gray-600">
                We provide the best hosting experience with cutting-edge technology
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-gray-600">
                  Using the latest AWS technology and infrastructure, we ensure you website is fast and reliable
                </p>
              </div>

              <div className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💻</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Experience</h3>
                <p className="text-gray-600">
                  White-glove onboarding and migration, support, and more
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure & Reliable</h3>
                <p className="text-gray-600">
                  Daily backups and SSL certificates included
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Support</h3>
                <p className="text-gray-600">
                  Support from our team of hosting experts that have provided hosting since 2018
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hosting Plans Section */}
        <div id="hosting-plans" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Choose Your Hosting Plan
              </h2>
              <p className="text-lg text-gray-600">
                Select the perfect plan for your website needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Object.entries(hostingPlans).map(([planId, plan]) => (
                <div key={planId} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    
                    <div className="mb-6">
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold text-gray-900">${plan.price.monthly}</span>
                        <span className="text-gray-500 ml-1">/month</span>
                      </div>
                      <div className="text-center mt-2">
                        <span className="text-2xl font-bold text-gray-900">${plan.price.yearly}</span>
                        <span className="text-gray-500 ml-1">/year</span>
                        <div className="text-green-600 text-sm">Save ${plan.savings.yearly}</div>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {plan.features.slice(0, 10).map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => handleAddToCart(planId, 'monthly')}
                        disabled={addingToCart[`${planId}-monthly`]}
                        className="w-full btn-secondary flex items-center justify-center"
                      >
                        {addingToCart[`${planId}-monthly`] ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <ShoppingCartIcon className="h-4 w-4 mr-2" />
                            Add Monthly to Cart
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleAddToCart(planId, 'yearly')}
                        disabled={addingToCart[`${planId}-yearly`]}
                        className="w-full btn-primary flex items-center justify-center"
                      >
                        {addingToCart[`${planId}-yearly`] ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <ShoppingCartIcon className="h-4 w-4 mr-2" />
                            Add Yearly to Cart
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Us Section */}
        <div id="contact" className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Contact Us
              </h2>
              <p className="text-lg text-gray-600 mb-2">
                Have questions? We'd love to hear from you!
              </p>
              <p className="text-lg text-gray-600">
                Email us at:{' '}
                <a 
                  href="mailto:info@nobigdealgroup.com" 
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  info@nobigdealgroup.com
                </a>
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg shadow-lg p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-gray-500 text-xs">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="contact-phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
                    placeholder="Your message..."
                  />
                </div>

                <div className="flex flex-col items-center gap-3">
                  <RecaptchaCheckbox ref={recaptchaRef} />
                  <button
                    type="submit"
                    disabled={isSubmittingContact}
                    className="btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingContact ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center max-w-md">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 text-primary-100">
              Join the many satisfied customers who trust The Host Bros
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cart"
                className="btn-secondary text-lg px-8 py-4"
              >
                View Cart
              </Link>
              <Link
                to={isAuthenticated ? "/dashboard" : "/register"}
                className="btn-primary text-lg px-8 py-4"
              >
                {isAuthenticated ? "Go to Dashboard" : "Create Account"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage; 